// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import * as moment from 'moment-timezone';
import { DxFormComponent, DxTagBoxComponent } from 'devextreme-angular';
import { plainToClass } from 'class-transformer';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { Editor } from 'tinymce';
import { Subscription } from 'rxjs';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { HtmlEditorService } from '../../../_services/shared-services/html-editor.service';
import { PortalAnnouncementService } from '../../../_services/data-services/portal-announcement.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';

// Model imports
import { AnnouncementEditorResultDto } from '../../../_models/announcement-editor-result-dto';
import { AnnouncementRawDto } from '../../../_models/announcement-raw-dto';
import { KeyValueResultDto } from '../../../_models/key-value-result-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';

@Component({
    selector: 'app-portal-announcement-editor',
    templateUrl: './portal-announcement-editor.component.html',
    styleUrls: ['./portal-announcement-editor.component.scss']
})
export class PortalAnnouncementEditorComponent implements OnInit, OnDestroy {
    @ViewChild('editorForm', { static: false }) editorForm: DxFormComponent;
    @ViewChild('multiSelector', { static: false }) multiSelector: DxTagBoxComponent;
    @ViewChild('htmlEditor', { static: false }) htmlEditor: EditorComponent;

    @Input() visible = false;
    @Input() isResidentAnnouncement = false;
    @Input() announcementId: number;
    @Output() onClose = new EventEmitter<void>();
    @Output() onRefresh = new EventEmitter<void>();

    loadingVisible = false;
    todayDate: Date;
    iconSave = IconConstants.SAVE;
    iconCancel = IconConstants.CANCEL;
    iconClose = IconConstants.CLOSE;

    titleText: string;
    editorResult: AnnouncementEditorResultDto;
    editorData: AnnouncementRawDto;
    editorOptions: any;
    audiencesCopy: KeyValueResultDto[];

    selectedContext: PropertyCommunityInfoResultDto;
    showEmailInFullScreen = false;

    contextManagerSubscription: Subscription;

    constructor(
        private readonly helperService: HelperService,
        private readonly htmlEditorService: HtmlEditorService,
        private readonly annoucementService: PortalAnnouncementService,
        private readonly errorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService
    ) {
        this.onSaveClick = this.onSaveClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
    }

    ngOnInit(): void {
        this.todayDate = moment().startOf('day').toDate();
        this.titleText = `${this.announcementId ? 'Edit' : 'Add New'} Announcement`;
        this.editorOptions = this.htmlEditorService.getEditorOptions({
            height: 375,
            onContentChanged: (event) => this.onContentChanged(event),
            onFullScreenStateChanged: (editor) => this.onFullScreenStateValueChanged(editor)
        });
        this.populateEditorResult();

        this.updateSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.contextManagerSubscription)) {
            this.contextManagerSubscription.unsubscribe();
        }
    }

    onShown(e: any): void {
        e.component.repaint();
        setTimeout(this.onResizeEnd.bind(this), 1000);
    }

    onHidden(): void {
        this.visible = false;
        this.onClose.emit();
    }

    onResizeEnd(): void {
        const elm = document.getElementById('portal-announcement-editor-content') as HTMLDivElement;
        if (!!this.htmlEditor && this.htmlEditor.editor) {
            const editor = this.htmlEditor.editor as Editor;
            if (!!editor.editorContainer && Utils.isFalse(this.showEmailInFullScreen)) {
                editor.editorContainer.style.height = `${Math.max(240, elm.clientHeight - 225)}px`;
            } else {
                editor.editorContainer.style.height = `${Math.max(240, elm.clientHeight)}px`;
            }
        }
    }

    onContextSelection(contexts: string[]): void {
        this.editorData.contextList = contexts;
        this.updateAudienceList(this.editorData.contextList[0]);
    }

    onSaveClick(): void {
        const onSuccess = () => {
            this.loadingVisible = false;
            this.onRefresh.emit();
            this.onHidden();
        };
        const onError = (error) => {
            this.loadingVisible = false;
            this.errorService.displayError(error);
        };
        const validator = this.editorForm.instance.validate();
        if (validator.isValid) {
            if (!this.announcementId && this.editorData.releaseDate < this.todayDate) {
                this.errorService.updateSubjectErrorMessage(`Release date must be either today or in future`);
                return;
            }
            if (this.editorData.expirationDate <= this.editorData.releaseDate) {
                this.errorService.updateSubjectErrorMessage(`Expiration date must be after release date`);
                return;
            }
            const dialog = this.helperService.getConfirmationDialog(
                'Confirmation',
                this.getConfirmationMessage(!!this.announcementId ? 'Create' : 'Update')
            );
            dialog.show().then((answer: boolean) => {
                if (answer) {
                    this.loadingVisible = true;
                    if (!!this.announcementId) {
                        this.annoucementService.updateAnnouncement(this.editorData).subscribe(onSuccess, onError);
                    } else {
                        this.annoucementService.saveAnnouncement(this.editorData).subscribe(onSuccess, onError);
                    }
                }
            });
        }
    }

    onCloseClick(): void {
        this.onHidden();
    }

    onContentChanged(event: any): void {
        this.editorData.content = event.target.getContent();
    }

    onFullScreenStateValueChanged(event: any): void {
        if (Utils.notNullAndDefined(event)) {
            if (Utils.isTrue(event.state) && Utils.isFalse(this.showEmailInFullScreen)) {
                this.showEmailInFullScreen = event.state;
                setTimeout(() => {
                    this.onResizeEnd();
                }, 200);
            } else {
                this.showEmailInFullScreen = false;
                setTimeout(() => {
                    this.onResizeEnd();
                }, 200);
            }
        }
    }

    private getConfirmationMessage(suffix: string): string {
        const contextType = this.editorData.contextList[0] || '';
        let message = `This is a community specific announcement. Only selected communities will see this announcement. ${suffix} announcement?`;
        if (contextType.startsWith('G')) {
            message = `This is not a community specific announcement. All communities will see this announcement. ${suffix} announcement?`;
        } else if (contextType.startsWith('M')) {
            message = `This is not a community specific announcement. All communities within the selected management companies will see this announcement. ${suffix} announcement?`;
        } else if (contextType.startsWith('L')) {
            message = `This is not a community specific announcement. All communities within the selected locations will see this announcement. ${suffix} announcement?`;
        } else if (this.editorData.contextList.length > 1) {
            message = `This is not a community specific announcement. All selected communities will see this announcement. ${suffix} announcement?`;
        }
        return message;
    }

    private populateEditorResult(): void {
        this.loadingVisible = true;
        this.annoucementService.getAnnouncementEditor(this.announcementId).subscribe(
            (result) => {
                if (!this.announcementId) {
                    result.announcementData.audienceCodes = [];
                    result.announcementData.contextList = [];
                }
                this.editorData = result.announcementData;
                this.editorResult = result;
                this.audiencesCopy = plainToClass(
                    KeyValueResultDto,
                    JSON.parse(JSON.stringify(result.audiences)) as any[]
                );
                this.updateAudienceList(result.announcementData.contextList[0]);

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private updateAudienceList(context: string): void {
        const isGlobalContext = (context || '').startsWith('G') || Utils.isNullOrUndefined(this.selectedContext);
        const validCodes = this.isResidentAnnouncement ? ['R'] : ['I', 'M', 'R'];
        this.editorResult.audiences = isGlobalContext
            ? [...this.audiencesCopy]
            : this.audiencesCopy.filter((a) => validCodes.indexOf(a.key) !== -1);
        this.editorData.audienceCodes = this.editorData.audienceCodes.filter(
            (a) => this.editorResult.audiences.findIndex((r) => r.key === a) !== -1
        );
        if (!!this.multiSelector && !!this.multiSelector.instance) {
            this.multiSelector.instance.repaint();
        }
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();
    }
}
